// Confirmation modal shown when the user tries to navigate away with
// unsaved edits in the editor buffer. Three actions:
//   - Save and continue → save() then run the pending action
//   - Discard           → run the pending action without saving
//   - Cancel            → close, no-op
//
// State is owned by useUnsavedGuard; this component renders.

import { useEffect, useRef } from 'react';
import type { UnsavedPrompt } from '../hooks/useUnsavedGuard';

type Props = {
  prompt: UnsavedPrompt;
};

export function UnsavedChangesModal({ prompt }: Props) {
  const saveBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (prompt.open) saveBtnRef.current?.focus();
  }, [prompt.open]);

  // Esc closes (treats as Cancel).
  useEffect(() => {
    if (!prompt.open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') prompt.onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prompt]);

  if (!prompt.open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={prompt.onCancel}
    >
      <div
        className="w-[90vw] max-w-sm rounded-md border border-zinc-800 bg-zinc-950 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="unsaved-title" className="text-zinc-100 text-base font-medium mb-2">
          Unsaved changes
        </h2>
        <p className="text-zinc-400 text-sm mb-5">
          Your edits haven&apos;t been saved. Save them before leaving?
        </p>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={prompt.onCancel}
            className="px-3 py-1.5 text-sm text-zinc-300 hover:text-zinc-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={prompt.onDiscard}
            className="px-3 py-1.5 text-sm rounded border border-zinc-800 text-zinc-300 hover:bg-zinc-900"
          >
            Discard
          </button>
          <button
            ref={saveBtnRef}
            type="button"
            onClick={prompt.onSave}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-500"
          >
            Save and continue
          </button>
        </div>
      </div>
    </div>
  );
}
